import type { TabViewItem } from "@/ui/tab-view";

import { Card, Col, Row, Empty } from "antd";
import { useTranslation } from "react-i18next";

import BaseHeader from "@/components/Header/BaseHeader";
import ActivitiesTable from "@/components/ListTable/ActivitiesTable";
import { LAZY_TABLE_FULL_PAGE_GUTTER, TABS_HEIGHT } from "@/constants/Layout";
import { STORAGE_KEYS } from "@/constants/StorageKeys";
import { TAB_KEYS } from "@/constants/TabKeys";
import { useGetActivity } from "@/entities/activity";
import { getActivityBatchUUID } from "@/modules/activity";
import { ActivityContext } from "@/modules/activity/activity-context";
import { AppLayoutPage } from "@/modules/app-layout/app-layout-page";
import { ListProvider } from "@/modules/list";
import { PageItemGate } from "@/modules/page-item-gate";
import { useIdParam } from "@/services/router";
import { DiffViewer, diffViewerFormatter } from "@/ui/diff-viewer";
import { TabView } from "@/ui/tab-view";

const TABLE_VH_DIFF = LAZY_TABLE_FULL_PAGE_GUTTER + TABS_HEIGHT;

export const ActivityLog = () => {
  const { t } = useTranslation();
  const id = useIdParam();

  const { activity, isLoading, error } = useGetActivity(id);

  const properties = activity?.properties;
  const batchUUID = getActivityBatchUUID(activity);

  const [beforeChanges, afterChanges] = diffViewerFormatter(
    properties?.old ?? {},
    properties?.attributes ?? {},
    activity?.event === "deleted"
  );

  const emptyDiff = !beforeChanges && !afterChanges;

  const tabs: TabViewItem[] = [
    {
      key: TAB_KEYS.ACTIVITIES_CHANGES,
      label: t("activity:log.changes"),
      children: (
        <Row gutter={[16, 24]} justify="center">
          <Col span={10}>
            <Card title={t("activity:log.context")}>
              {id && <ActivityContext id={id} />}
            </Card>
          </Col>
          <Col span={14}>
            <Card title={t("activity:log.changes")} loading={isLoading}>
              {emptyDiff && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
              {!emptyDiff && (
                <DiffViewer oldValue={beforeChanges} newValue={afterChanges} />
              )}
            </Card>
          </Col>
        </Row>
      ),
    },
    {
      key: TAB_KEYS.ACTIVITIES_BATCH,
      label: t("global:batch"),
      children: (
        <ListProvider
          endpoint="activities"
          storageKey={STORAGE_KEYS.ACTIVITIES_BATCH}
          hiddenFilters={{ "filter[batch_uuid]": batchUUID ?? "" }}
          lazy={{ scrollY: { vhDiff: TABLE_VH_DIFF } }}
          pageSize={15}
        >
          <ActivitiesTable />
        </ListProvider>
      ),
      hidden: !batchUUID,
    },
  ];

  return (
    <PageItemGate model="activity" queryError={error}>
      <BaseHeader title={t("activity:log.title", { id })} loading={isLoading} />
      <AppLayoutPage>
        <TabView isNav tabs={tabs} defaultActiveKey="changes" />
      </AppLayoutPage>
    </PageItemGate>
  );
};
